import { useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth/tenant-context';
import { authMeQueryOptions } from '@/api/auth/auth-query';
import {
  connectSocket,
  disconnectSocket,
  joinTenant,
  leaveTenant,
  getSocket,
} from '@/lib/websocket/socket-client';
import type {
  EntityChangeEvent,
  NotificationEvent,
} from '@/lib/websocket/types';
import {
  invalidateQueriesForEntity,
  invalidateQueriesForEntityChange,
} from '@/lib/websocket/entity-change-invalidation';
import { getQueryKeysToInvalidateForNotification } from '@/lib/websocket/notification-invalidation';
import { notificationQueryKeys } from '@/api/notifications/query-keys';
import { tenantSettingsQueryKeys } from '@/api/tenants-settings/query-keys';

export function useWebSocket() {
  const { selectedTenant } = useTenant();
  const queryClient = useQueryClient();
  const previousTenantRef = useRef<string | null>(null);
  // `useQuery` e non `getQueryData`: la stanza da chiedere dipende da questi
  // dati, quindi servono in un valore REATTIVO — letti dalla cache una volta
  // sola, un arrivo tardivo non farebbe ri-renderizzare e la connessione non
  // partirebbe mai. Nessuna richiesta in più: la stessa query è già montata da
  // TenantProvider.
  const { data: authData } = useQuery(authMeQueryOptions);
  const currentUserId = authData?.authenticated
    ? (authData.user as { id?: string } | undefined)?.id
    : undefined;
  // I tenant della SESSIONE corrente, che non coincidono sempre col tenant
  // selezionato in questa tab: l'impersonation sostituisce il cookie per tutto
  // il browser, quindi una tab lasciata aperta sul tenant di prima continua a
  // chiedere una stanza che la nuova sessione non ha. Chiederla comunque non
  // la fa entrare — produce solo un rifiuto a ogni riconnessione.
  const sessionTenantIds = authData?.authenticated
    ? ((authData.tenants ?? []) as { id?: string }[]).map((tenant) => tenant.id)
    : [];
  const canJoinSelectedTenant =
    !!selectedTenant?.id && sessionTenantIds.includes(selectedTenant.id);

  const handleNotification = useCallback(
    (event: NotificationEvent) => {
      if (!selectedTenant?.id || event.tenantId !== selectedTenant.id) {
        return;
      }
      if (currentUserId && event.userId !== currentUserId) {
        return;
      }

      // Quali code rileggere dipende dal tipo di notifica: la decisione sta
      // in una funzione pura, testata a parte.
      getQueryKeysToInvalidateForNotification(event, selectedTenant.id).forEach(
        (queryKey) => queryClient.invalidateQueries({ queryKey })
      );
    },
    [queryClient, selectedTenant?.id, currentUserId]
  );

  const handleEntityChange = useCallback(
    (event: EntityChangeEvent) =>
      invalidateQueriesForEntityChange(queryClient, event),
    [queryClient]
  );

  useEffect(() => {
    if (!selectedTenant?.id || !canJoinSelectedTenant) {
      return;
    }

    const socket = connectSocket();

    // If tenant changed, leave old room and join new one
    if (
      previousTenantRef.current &&
      previousTenantRef.current !== selectedTenant.id
    ) {
      leaveTenant(previousTenantRef.current);
    }

    const handleConnect = () => {
      joinTenant(selectedTenant.id).then((result) => {
        if (result.success) {
          queryClient.invalidateQueries({
            queryKey: tenantSettingsQueryKeys.detail(selectedTenant.id)
              .queryKey,
          });
          // Safety net after reconnect: refresh notification cache in case of
          // missed socket events while offline.
          queryClient.invalidateQueries({
            queryKey: notificationQueryKeys.all(selectedTenant.id),
          });
          invalidateQueriesForEntity(
            queryClient,
            'document',
            selectedTenant.id
          );
        }
      });
    };

    socket.on('connect', handleConnect);
    if (socket.connected) {
      handleConnect();
    }

    // Listen for notifications (always enabled)
    socket.on('notification', handleNotification);

    // I cambi di entità includono anche gli esiti SDI dopo il commit.
    socket.on('entity-change', handleEntityChange);

    // Error handling
    const handleConnectError = () => undefined;
    const handleDisconnect = () => undefined;

    socket.on('connect_error', handleConnectError);
    socket.on('disconnect', handleDisconnect);

    previousTenantRef.current = selectedTenant.id;

    return () => {
      socket.off('connect', handleConnect);
      socket.off('notification', handleNotification);
      socket.off('connect_error', handleConnectError);
      socket.off('disconnect', handleDisconnect);
      socket.off('entity-change', handleEntityChange);
    };
  }, [
    selectedTenant?.id,
    canJoinSelectedTenant,
    handleEntityChange,
    handleNotification,
    queryClient,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previousTenantRef.current) {
        leaveTenant(previousTenantRef.current);
      }
      disconnectSocket();
    };
  }, []);

  return {
    isConnected: getSocket()?.connected ?? false,
  };
}
